/**
 * segmentConfig.js — per-schema configuration for the "segment" filter.
 *
 * Background: "segment" started life as one thing — the seven attitudinal MRP
 * segments stored in the tracking schema's `segment` column — so its column
 * name, its codes, and its display labels were all hard-coded in a handful of
 * places (Sidebar.js twice, mobile/FiltersSheet.js, DEMO_LABELS).
 *
 * A secondary schema does not have to agree. The `energy` schema breaks
 * respondents down by their electricity supplier, stored in
 * `electricity_provider` with an entirely different code set. Conceptually it
 * occupies the same slot in the UI (one multi-select group of "which kind of
 * respondent am I looking at"), so rather than add a parallel filter dimension
 * we make the existing one schema-aware.
 *
 * Contract with the backend
 * -------------------------
 * The frontend always posts the filter under the generic key `segment`
 * (`filters: { segment: [4] }`). The backend resolves that key to the physical
 * column for the requested schema via SCHEMA_SEGMENT_COL in core/views.py.
 * The `column` values below are documentation of that mapping — the backend map
 * is authoritative, so keep the two in sync when onboarding a schema.
 *
 * Because the codes are only meaningful relative to a schema, callers must
 * always resolve labels through this module rather than assuming the tracking
 * set. Code 4 is "Nostalgic Populists" under `tracking` and "Octopus Energy"
 * under `energy`.
 */

/** Schema assumed when a group omits one — mirrors DEFAULT_SCHEMA in schemaTypes.js. */
export const DEFAULT_SEGMENT_SCHEMA = "tracking";

/**
 * Per-schema segment definitions.
 *
 *   column   — physical DB column (documentation; backend owns the real mapping)
 *   label    — heading shown above the chip group
 *   minWave  — when set, the filter is only offered for questions that carry
 *              this wave, because earlier waves have no values in the column.
 *              Omit when every wave in the schema has the column populated.
 *   options  — [code, label] pairs in display order
 */
export const SEGMENT_CONFIGS = {
  tracking: {
    column: "segment",
    label: "Segment",
    // Attitudinal segments were first modelled at wave 4.
    minWave: 4,
    // Display order is deliberately non-sequential (kept from Sidebar.js).
    options: [
      [2, "Progressive Idealists"],
      [3, "Open Liberals"],
      [5, "Hard Bargainers"],
      [7, "Detached Middle"],
      [6, "Disenchanted & Restless"],
      [1, "Comfortable Traditionalists"],
      [4, "Nostalgic Populists"],
    ],
  },

  energy: {
    column: "electricity_provider",
    label: "Electricity provider",
    options: [
      [8, "British Gas"],
      [5, "E.ON / E.ON Next"],
      [6, "EDF Energy"],
      [4, "Octopus Energy"],
      [2, "Ovo Energy"],
      [1, "Scottish Power"],
      // [3, "Other"],
      // [7, "Don't know"],
    ],
  },

   mccain: {
    column: "voting_intention",
    label: "Voting Intention",
    // Display order is deliberately non-sequential (kept from Sidebar.js).
    options: [
      [1, "Conservative"],
      [2, "Labour"],
      [3, "Liberal Democrats"],
      [4, "Reform"],
      [5, "Green"],
      [6, "Independent / Other"],
      [7, "SNP"],
      [8, "Plaid Cymru"],
      [9, "Restore Britain"],
      [10, "Will not vote"],
      [11, "Don't know"],
    ],
  },

};

/**
 * Party colours for the McCain VI codes, mirroring the `vote_intention` palette
 * in polAggregators/political.js. Kept here so the code→colour mapping lives
 * next to the code→label mapping rather than being re-keyed per report view.
 */
export const MCCAIN_VI_COLORS = {
  1: "#0087DC",  // Conservative
  2: "#D50000",  // Labour
  3: "#FDBB30",  // Liberal Democrats
  4: "#12B6CF",  // Reform
  5: "#008066",  // Green
  6: "#777777",  // Independent / Other
  7: "#FFF95D",  // SNP
  8: "#3F8428",  // Plaid Cymru
  9: "#000080",  // Restore Britain
  10: "#9CA3AF", // Will not vote
  11: "#CCCCCC", // Don't know
};

/**
 * Black or white, whichever reads against `hex`. Computed rather than listed
 * per party: Labour's red and Reform's cyan fall on opposite sides of the
 * threshold, and any party added later gets the right answer for free.
 * sRGB relative luminance, WCAG definition.
 */
export function readableOn(hex) {
  const h = String(hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (full.length !== 6) return "#ffffff";
  const chan = (i) => {
    const c = parseInt(full.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * chan(0) + 0.7152 * chan(2) + 0.0722 * chan(4);
  return L > 0.45 ? "#0f172a" : "#ffffff";
}

/**
 * The VI cuts offered as McCain *reports*, as opposed to as an in-app filter.
 * Deliberately narrower than SEGMENT_CONFIGS.mccain.options: the report picker
 * starts with the three parties the client asked for, plus the unfiltered "All
 * voters" version the picker adds itself. Widening it is a matter of adding
 * codes here — every other party is already labelled and queryable, and the
 * full eleven remain available as an in-app filter.
 */
export const MCCAIN_REPORT_VI_CODES = [
  1,  // Conservative
  2,  // Labour
  4,  // Reform
];

/** Resolve a schema string to its segment config, or null if it has none. */
export function segmentConfigFor(schema) {
  const resolved =
    (typeof schema === "string" && schema.trim()) || DEFAULT_SEGMENT_SCHEMA;
  return SEGMENT_CONFIGS[resolved] || null;
}

/** Physical column name for the schema's segment filter. */
export function segmentColumnFor(schema) {
  return segmentConfigFor(schema)?.column || null;
}

/** Chip-group heading, e.g. "Segment" / "Electricity provider". */
export function segmentFilterLabel(schema) {
  return segmentConfigFor(schema)?.label || "Segment";
}

/** `[code, label]` pairs in display order (empty when the schema has none). */
export function segmentOptionsFor(schema) {
  return segmentConfigFor(schema)?.options || [];
}

/** `{ code: label }` map for the schema — the shape DEMO_LABELS uses. */
export function segmentLabelsFor(schema) {
  return Object.fromEntries(segmentOptionsFor(schema));
}

/** Label for one code under `schema`, falling back to the raw code. */
export function segmentLabel(schema, value) {
  return segmentLabelsFor(schema)[value] ?? String(value);
}

/**
 * Should the segment chip group be offered for this question?
 *
 * A schema with `minWave` only has segment values from that wave onwards, so
 * the group is hidden for questions that predate it. `isClientSection` is the
 * desktop escape hatch that has always shown the group on client pages
 * regardless of wave — pass it from Sidebar, omit it on mobile.
 */
export function isSegmentFilterAvailable(group, { isClientSection = false } = {}) {
  const cfg = segmentConfigFor(group?.schema);
  if (!cfg) return false;
  if (cfg.minWave == null) return true;
  if (isClientSection) return true;
  return Array.isArray(group?.waves) && group.waves.includes(cfg.minWave);
}

/**
 * True when two schemas disagree on what a segment code means, i.e. an active
 * segment filter must be cleared when moving between them. Comparing the
 * physical column (rather than the schema name) keeps sibling schemas that
 * share a column from pointlessly resetting the user's filter.
 */
export function segmentFilterCarriesOver(fromSchema, toSchema) {
  return segmentColumnFor(fromSchema) === segmentColumnFor(toSchema);
}
